import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchArticles,
  callPublishMedia,
} from "@/store/media/media.action";
import { Button, toast } from "..";
import {
  generateThumbnailUrl,
  getHostLogoStyleByHostId,
  getDenmarkStatusIndicatorData,
  openVideoInNewTab,
  formatTimestampToAgo,
} from "../../utils";
import { RootState } from "../../store";
import SandboxIcon from "../../assets/icons/svg/hourglass.svg?react";
import DenmarkPublishPopover from "../MediaPublish/DenmarkPublish/DenmarkPublishPopover";
import { getHostLogoStyle } from "../../utils";
import FailureThumb from "../../assets/icons/svg/failure.svg";

interface ArticleSearchResultsProps {
  searchQuery: string;
  onViewAll?: () => void;
  showInlineResults?: boolean;
  maxInlineResults?: number;
  hostid?: string;
  selectedArticles?: Article[];
  onSelectedArticlesChange?: (articles: Article[]) => void;
  onArticlePreview?: (article: Article) => void;
  showActionButtons?: boolean;
  isInModal?: boolean;
  suggestedArticles?: Article[];
  mediaId?: string;
  hosts?: any[];
  denmarkHosts?: any[];
  socialPublishData?: any[];
  onCloseSearch?: () => void;
  onAddDenmarkEmbedChip?: (article: Article, type: "lead" | "embed") => void;
  formikDenmarkValues?: any[];
}

interface Article {
  id: string;
  title: string;
  description: string;
  thumbnail?: string;
  author?: string;
  publish_s_date?: string;
  path?: string;
  score?: number;
  selectedType?: "lead" | "embed" | null;
  selectedTypes?: ("lead" | "embed")[];
  msid?: string;
  isVideoEligible?: boolean;
  status?: number;
  mt?: number;
  ct?: number;
  channel_name?: string;
}

const ArticleSearchResults: React.FC<ArticleSearchResultsProps> = ({
  searchQuery,
  onViewAll,
  showInlineResults = true,
  maxInlineResults = 5,
  hostid,
  selectedArticles: externalSelectedArticles = [],
  onSelectedArticlesChange,
  onArticlePreview,
  showActionButtons = false,
  isInModal = false,
  suggestedArticles = [],
  mediaId,
  hosts = [],
  socialPublishData = [],
  onCloseSearch,
  onAddDenmarkEmbedChip,
  formikDenmarkValues = [],
  denmarkHosts = [],
}) => {
  const dispatch = useDispatch();
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedArticles, setSelectedArticles] = useState<Article[]>(
    externalSelectedArticles
  );
  const [loading, setLoading] = useState(isInModal ? true : false);
  const [error, setError] = useState<string | null>(null);
  const [articlePopoverChip, setArticlePopoverChip] = useState<{
    chip: any;
    anchor: DOMRect;
  } | null>(null);
  const prevExternalSelectedArticles = useRef<Article[]>(
    externalSelectedArticles
  );
  const currentMediaDetailsForEdit = useSelector(
    (state: RootState) => state.media.mediaDetails.media
  );

  const getSocialItemForArticle = (article: Article) => {
    const found = socialPublishData.find((social) => {
      const matchesById = social.id?.toString() === article.id;
      const matchesBySection = social.section?.toString() === article.id;
      const matchesByMsid = social.id?.toString() === article.msid;
      const matchesBySectionMsid = social.section?.toString() === article.msid;
      const isDenmarkEmbed = social.job_type === "denmark_embed";
      return (
        (matchesById ||
          matchesBySection ||
          matchesByMsid ||
          matchesBySectionMsid) &&
        isDenmarkEmbed
      );
    });
    return found;
  };

  const handleArticleClick = async (
    article: Article,
    type: "lead" | "embed"
  ) => {
    const publishedDenmark = currentMediaDetailsForEdit?.socials?.find(
      (social: any) => social.job_type === "denmark" && social.status === 10
    );

    if (publishedDenmark?.published_id) {
      const payload = {
        job_type: "denmark_embed",
        platform: "denmark",
        host: hostid ? parseInt(hostid) : 0,
        section: Number(article.msid),
        media: mediaId,
        title: currentMediaDetailsForEdit?.title,
        desc:
          currentMediaDetailsForEdit?.desc || currentMediaDetailsForEdit?.title,
        tags: currentMediaDetailsForEdit?.tags,
        upload_type: type,
        channel_name: article.title,
        msid: Number(publishedDenmark.published_id),
      };
      try {
        dispatch(
          callPublishMedia(payload as any, (response: any) => {
            if (response.status !== 200) {
              toast.fail(
                `Failed to set article as ${type === "lead" ? "Lead" : "Embed"}`
              );
            } else {
              toast.success(
                `Article set as ${type === "lead" ? "Lead" : "Embed"}`
              );
            }
            onCloseSearch?.();
          }) as any
        );
      } catch (error) {
        toast.fail(
          `Failed to set article as ${type === "lead" ? "Lead" : "Embed"}`
        );
      }
    } else {
      if (onAddDenmarkEmbedChip) {
        onAddDenmarkEmbedChip(article, type);
        handleSelectArticle(article, type);
        toast.success(
          `Article will be set as ${type === "lead" ? "Lead" : "Embed"} on save`
        );
        onCloseSearch?.();
      } else {
        toast.fail("Unable to add article. Please try again.");
      }
    }
  };

  const handleSelectArticle = (article: Article, type: "lead" | "embed") => {
    const existingIndex = selectedArticles.findIndex(
      (a) => a.id === article.id
    );

    let updatedArticles;
    if (existingIndex !== -1) {
      const existingArticle = selectedArticles[existingIndex];
      const existingTypes =
        existingArticle.selectedTypes ||
        [existingArticle.selectedType].filter(
          (t): t is "lead" | "embed" => t === "lead" || t === "embed"
        );

      if (!existingTypes.includes(type)) {
        const newTypes = [...existingTypes, type];
        updatedArticles = [...selectedArticles];
        updatedArticles[existingIndex] = {
          ...existingArticle,
          selectedTypes: newTypes,
          selectedType: newTypes[newTypes.length - 1],
        };
      } else {
        updatedArticles = selectedArticles;
      }
    } else {
      const selectedArticle = {
        ...article,
        selectedTypes: [type],
        selectedType: type,
      };
      updatedArticles = [...selectedArticles, selectedArticle];
    }

    setSelectedArticles(updatedArticles);
    onSelectedArticlesChange?.(updatedArticles);
  };

  useEffect(() => {
    const hasChanged =
      prevExternalSelectedArticles.current.length !==
        externalSelectedArticles.length ||
      prevExternalSelectedArticles.current.some(
        (article, index) =>
          !externalSelectedArticles[index] ||
          article.id !== externalSelectedArticles[index].id
      );

    if (hasChanged) {
      setSelectedArticles(externalSelectedArticles);
      prevExternalSelectedArticles.current = externalSelectedArticles;
    }
  }, [externalSelectedArticles]);

  const getScoreColor = (percentage: number) => {
    if (percentage >= 100) return "#28a745";
    if (percentage >= 80) return "#20c997";
    if (percentage >= 60) return "#ffc107";
    if (percentage >= 40) return "#fd7e14";
    return "#dc3545";
  };

  useEffect(() => {
    const searchArticles = async () => {
      if (!searchQuery.trim()) {
        setArticles([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await dispatch(
          fetchArticles({
            keyword: searchQuery,
            hostid: hostid,
          }) as any
        );
        if (response && response.data && response.data.length > 0) {
          const mappedArticles: Article[] = response.data.map((item: any) => ({
            id: item.msid?.toString() || item.parentid?.toString() || "",
            title: item.title,
            description: item.synopsis || "No description available",
            publish_s_date: item.publish_s_date,
            path: item.path || "",
            msid: item.msid?.toString() || "",
            isVideoEligible: item.isVideoEligible,
          }));
          setArticles(mappedArticles);
        } else {
          setArticles([]);
        }
      } catch (error) {
        toast.fail("Failed to search articles");
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    const timeoutId = setTimeout(searchArticles, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch, hostid]);

  if (
    articles.length === 0 &&
    suggestedArticles.length === 0 &&
    selectedArticles.length === 0 &&
    !loading &&
    !searchQuery.trim()
  ) {
    return null;
  }

  const sourceArticles =
    suggestedArticles.length > 0 ? suggestedArticles : articles;
  const filteredArticles = sourceArticles.filter(
    (article) => !selectedArticles.find((s) => s.id === article.id)
  );

  const displayArticles = showInlineResults
    ? filteredArticles.slice(0, maxInlineResults)
    : filteredArticles;
  const renderArticleCard = (
    article: Article,
    index: number,
    isSelected: boolean
  ) => (
    <div
      key={`${isSelected ? "selected" : "article"}-${article.id || index}`}
      className={`d-flex gap-2 align-items-start articleList ${
        isSelected ? "active" : ""
      }`}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onArticlePreview) {
          onArticlePreview(article);
        }
      }}
    >
      <div className="articleThumbnail">
        <div className="ratio ratio-16x9">
          <img
            src={generateThumbnailUrl(article.id, Number(hostid))}
            alt="Article"
            style={{ objectFit: "contain" }}
            onError={(e) => {
              e.currentTarget.src = FailureThumb;
            }}
          />
        </div>
      </div>
      <div
        className="flex-grow-1"
        style={{
          maxWidth: showInlineResults
            ? "calc(100% - 80px)"
            : "calc(100% - 190px)",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            className="fw-medium text-truncate text-primary"
            style={{
              fontSize: "12px",
              cursor: "pointer",
              position: "relative",
              zIndex: 10,
            }}
          >
            {article.title}
          </div>
        </div>
        {article.publish_s_date && (
          <div className="text-muted small text-truncate">
            {formatTimestampToAgo(Number(article.publish_s_date))}
          </div>
        )}
        {article.path && (
          <div className="text-muted line-clamp-1 small">{article.path}</div>
        )}
        <div className="text-muted small text-truncate">
          {article.description}
        </div>
      </div>
      {article.score !== undefined && (
        <div className="d-flex flex-column align-items-center gap-1">
          <div
            className="position-relative"
            style={{ width: "30px", height: "30px" }}
          >
            <svg
              className="position-absolute"
              width="30"
              height="30"
              style={{ transform: "rotate(-90deg)" }}
            >
              <circle
                cx="15"
                cy="15"
                r="12"
                stroke="#e9ecef"
                strokeWidth="2"
                fill="none"
              />
              <circle
                cx="15"
                cy="15"
                r="12"
                stroke={getScoreColor(article.score)}
                strokeWidth="2"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 12}`}
                strokeDashoffset={`${
                  2 * Math.PI * 12 * (1 - article.score / 100)
                }`}
                strokeLinecap="round"
              />
            </svg>
            <div className="position-absolute top-50 start-50 translate-middle text-center">
              <div
                className="fw-bold"
                style={{
                  color: getScoreColor(article.score),
                  fontSize: "10px",
                }}
              >
                {article.score}
              </div>
            </div>
          </div>
          <div className="text-muted" style={{ fontSize: "8px" }}>
            Score
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      style={{ height: isInModal ? "" : "auto" }}
      className={` ${
        !isInModal
          ? "position-absolute article-search-mini"
          : "article-search-results"
      }`}
    >
      {isInModal && selectedArticles.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-2 align-items-center border-bottom pb-3">
          <b>Selected</b>
          {selectedArticles.map((article, index) => {
            const socialItem = getSocialItemForArticle(article);
            let latestSocialItem = socialItem;
            if (currentMediaDetailsForEdit?.socials) {
              const matchingSocial = currentMediaDetailsForEdit.socials.find(
                (social: any) => {
                  if (social.id?.toString() === article.id) return true;
                  if (
                    social.section?.toString() === article.msid &&
                    social.job_type === "denmark_embed"
                  )
                    return true;
                  if (
                    social.id?.toString() === article.msid &&
                    social.job_type === "denmark_embed"
                  )
                    return true;
                  return false;
                }
              );
              if (matchingSocial) {
                latestSocialItem = matchingSocial;
              }
            }

            const isTemporary =
              !latestSocialItem || latestSocialItem.status === 100;
            const hostToUse =
              latestSocialItem?.host?.toString() ||
              socialItem?.host?.toString() ||
              hostid;
            const hostLogoStyle = getHostLogoStyleByHostId(
              hostToUse,
              hosts,
              undefined,
              denmarkHosts
            );
            return (
              <div
                key={`chip-${article.id}-${index}`}
                className="d-flex align-items-center gap-2 social-chip px-2 py-1 border rounded-pill"
                style={{
                  cursor: isTemporary ? "default" : "pointer",
                  position: "relative",
                }}
                onClick={(e) => {
                  if (!isTemporary) {
                    e.stopPropagation();
                    e.preventDefault();
                    const rect = e.currentTarget.getBoundingClientRect();

                    const chipData = latestSocialItem
                      ? { ...latestSocialItem }
                      : {
                          id: article.id,
                          section: article.msid
                            ? Number(article.msid)
                            : article.id,
                          channel_name: article.channel_name,
                          host: parseInt(hostToUse || "0", 10),
                          job_type: "denmark_embed",
                          status: article.status || 100,
                          title: article.title,
                          desc: article.description || article.title,
                          mt: article.mt,
                          ct: article.ct,
                          platform: "denmark",
                        };
                    setArticlePopoverChip({
                      chip: chipData,
                      anchor: rect,
                    });
                  }
                }}
              >
                <div style={hostLogoStyle} />
                <span
                  className="text-truncate"
                  style={{ maxWidth: "200px", fontSize: "12px" }}
                >
                  {article.channel_name || article.title}
                </span>
                {isTemporary && (
                  <i
                    className="bi bi-x fs-5"
                    style={{ cursor: "pointer", marginLeft: "4px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const updated = selectedArticles.filter(
                        (a) => a.id !== article.id
                      );
                      setSelectedArticles(updated);
                      onSelectedArticlesChange?.(updated);
                    }}
                  />
                )}
                {!isTemporary && latestSocialItem && (
                  <div
                    className="ms-1"
                    style={{
                      display: "inline-block",
                      width: "10px",
                      height: "10px",
                    }}
                  >
                    {(() => {
                      const statusToUse = latestSocialItem.status;
                      const statusData =
                        getDenmarkStatusIndicatorData(statusToUse);
                      return statusData.type === "rotating" ? (
                        <SandboxIcon
                          className={statusData.className}
                          width={16}
                          height={16}
                          style={{ flexShrink: 0 }}
                        />
                      ) : (
                        <span
                          className={statusData.className}
                          style={statusData.style}
                        />
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {loading && (
        <div
          className={`${
            isInModal
              ? "d-flex align-items-center justify-content-center"
              : "text-center"
          } ${
            isInModal
              ? "position-absolute top-50 start-50 translate-middle"
              : "py-3"
          }`}
          style={isInModal ? { minHeight: "200px", width: "100%" } : {}}
        >
          <div className="d-flex flex-column align-items-center">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2 mt-4">Searching articles...</span>
          </div>
        </div>
      )}
      {error && !loading && (
        <div className="text-center py-3 text-danger">
          <div>{error}</div>
        </div>
      )}
      {!loading && !error && articles.length === 0 && searchQuery.trim() && (
        <div className="text-center py-3 text-muted">
          <div>No articles found for &quot;{searchQuery}&quot;</div>
        </div>
      )}
      {!loading &&
        displayArticles.map((article, index) => {
          const isSelected = selectedArticles.some((s) => s.id === article.id);
          return renderArticleCard(article, index, isSelected);
        })}
      {!loading &&
        showInlineResults &&
        filteredArticles.length > maxInlineResults &&
        onViewAll && (
          <div className="text-end">
            <Button
              type="outline-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onViewAll();
              }}
              className="border-0"
            >
              View All
            </Button>
          </div>
        )}

      {articlePopoverChip &&
        articlePopoverChip.chip.status !== 100 &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: articlePopoverChip.anchor.left - 45,
              top: articlePopoverChip.anchor.top - 105,
              zIndex: 9999,
              pointerEvents: "auto",
            }}
          >
            <DenmarkPublishPopover
              chip={articlePopoverChip.chip}
              anchor={articlePopoverChip.anchor}
              onClose={() => {
                setArticlePopoverChip(null);
              }}
              handleRouting={() => {
                if (articlePopoverChip?.chip.status === 10) {
                  openVideoInNewTab(
                    articlePopoverChip?.chip?.platform,
                    articlePopoverChip?.chip?.published_id
                  );
                }
              }}
              publishData={articlePopoverChip.chip}
              currentMediaDetailsForEdit={currentMediaDetailsForEdit}
              hostLogoStyle={
                hosts &&
                hosts.length > 0 &&
                hosts.find(
                  (host: any) =>
                    host.id === articlePopoverChip.chip.host?.toString()
                )
                  ? getHostLogoStyle(
                      hosts.find(
                        (host: any) =>
                          host.id === articlePopoverChip.chip.host?.toString()
                      )?.name || ""
                    )
                  : {}
              }
              left={articlePopoverChip.anchor.left}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default ArticleSearchResults;
